<template>
  <div class="insurance-types-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="el-icon-notebook-2"></i>
          险种管理
        </h1>
        <p class="page-subtitle">管理系统中的保险险种信息</p>
      </div>
    </div>

    <!-- 搜索区域 -->
    <el-card class="search-card" shadow="never">
      <div class="search-header">
        <div class="search-title">
          <i class="el-icon-search"></i>
          <span>搜索条件</span>
        </div>
      </div>

      <el-form :model="searchData" label-width="100px" class="search-form">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="险种名称：">
              <el-input
                v-model="searchData.categoryName"
                placeholder="请输入险种名称"
                clearable
                @keyup.enter.native="handleSearch"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="险种类型：">
              <el-select v-model="searchData.type" placeholder="请选择险种类型" clearable>
                <el-option label="交强险" :value="1"></el-option>
                <el-option label="商业险" :value="2"></el-option>
                <el-option label="附加险" :value="3"></el-option>
                <el-option label="代收车船税" :value="4"></el-option>
                <el-option label="其他" :value="5"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <div class="search-actions">
              <el-button @click="handleReset">重置</el-button>
              <el-button type="primary" @click="handleSearch">查询</el-button>
            </div>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 数据展示区域 -->
    <el-card class="data-card" shadow="never">
      <div class="data-header">
        <div class="data-title">
          <h3>险种列表</h3>
          <div class="data-stats">
            <span class="total-count">共 {{ total }} 条记录</span>
          </div>
        </div>
        <div class="data-actions">
          <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增险种</el-button>
        </div>
      </div>

      <!-- 表格 -->
      <div class="table-container">
        <el-table
          :data="listData"
          v-loading="listLoading"
          element-loading-text="数据加载中..."
          element-loading-spinner="el-icon-loading"
          element-loading-background="rgba(255, 255, 255, 0.8)"
          class="modern-table"
          :header-cell-style="{ background: '#f8f9fa', color: '#495057', fontWeight: '600' }"
        >
          <el-table-column label="险种名称" prop="categoryName" :min-width="200" fixed="left">
            <template slot-scope="scope">
              <div class="category-name-cell">
                <span class="category-name">{{ scope.row.categoryName }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="险种类型" prop="type" :min-width="120" align="center">
            <template slot-scope="scope">
              <el-tag size="small" :type="getTypeTagType(scope.row.type)" effect="light">
                {{ getTypeText(scope.row.type) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="险种说明" prop="description" :min-width="250" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="description-cell">
                {{ scope.row.description || "-" }}
              </div>
            </template>
          </el-table-column>

          <el-table-column label="险种案例" prop="caseDesc" :min-width="250" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="case-desc-cell">
                {{ scope.row.caseDesc || "-" }}
              </div>
            </template>
          </el-table-column>

          <el-table-column label="更新时间" prop="mtime" :min-width="180" align="center">
            <template slot-scope="scope">
              <div class="time-cell">
                {{ formatDateTime(scope.row.mtime) }}
              </div>
            </template>
          </el-table-column>

          <el-table-column label="操作" fixed="right" :min-width="150" align="center">
            <template slot-scope="scope">
              <div class="action-cell">
                <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
                <el-divider direction="vertical"></el-divider>
                <el-button type="text" size="small" class="danger-btn" @click="handleDelete(scope.row)">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          background
        >
        </el-pagination>
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="insuranceTypeVisible"
      width="600px"
      :close-on-click-modal="false"
      class="insurance-dialog"
    >
      <el-form
        :model="insuranceTypeForm"
        ref="insuranceTypeForm"
        :rules="insuranceTypeRule"
        label-width="100px"
        class="insurance-form"
      >
        <el-form-item label="险种名称：" prop="categoryName">
          <el-input
            v-model="insuranceTypeForm.categoryName"
            placeholder="请输入险种名称"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>

        <el-form-item label="险种类型：" prop="type">
          <el-radio-group v-model="insuranceTypeForm.type">
            <el-radio :label="1">交强险</el-radio>
            <el-radio :label="2">商业险</el-radio>
            <el-radio :label="3">附加险</el-radio>
            <el-radio :label="4">代收车船税</el-radio>
            <el-radio :label="5">其他</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="险种说明：" prop="description">
          <el-input
            v-model="insuranceTypeForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入险种说明"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>

        <el-form-item label="险种案例：" prop="caseDesc">
          <el-input
            v-model="insuranceTypeForm.caseDesc"
            type="textarea"
            :rows="3"
            placeholder="请输入险种案例"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogCancel">取 消</el-button>
        <el-button type="primary" @click="dialogConfirm">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/* eslint-disable */
import dayjs from 'dayjs';

export default {
  name: 'InsuranceTypesList',
  data() {
    return {
      insuranceTypeVisible: false,
      dialogType: 'add', // add 或 edit
      insuranceTypeForm: {
        categoryCode: '',   //险种code
        categoryName: '',   //险种名称
        description: '',    //险种说明
        caseDesc: '',       //险种案例
        type: null,         //险种类型
      },
      insuranceTypeRule: {
        categoryName: [{ required: true, message: '请输入险种名称', trigger: 'blur' }],
        type: [{ required: true, message: '请选择险种类型', trigger: 'change' }]
      },
      listData: [],
      // 搜索数据
      searchData: {
        categoryName: "", //险种名称
        type: null,       //险种类型
      },
      listLoading: false, //列表loading
      pageNum: 1,
      pageSize: 10,
      total: 0,
    };
  },
  computed: {
    dialogTitle() {
      return this.dialogType === 'add' ? '新增险种' : '编辑险种';
    }
  },
  filters: {
    stateFilter: function(val) {
      
    },
  },
  //监听属性 类似于data概念
  computed: {},
  //监控data中的数据变化
  watch: {},
  //方法集合
  methods: {
    // 格式化日期时间
    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss');
    },
    
    // 获取险种类型文本
    getTypeText(type) {
      const typeMap = {
        1: '交强险',
        2: '商业险',
        3: '附加险',
        4: '代收车船税',
        5: '其他'
      };
      return typeMap[type] || '未知';
    },
    
    // 获取险种类型标签样式
    getTypeTagType(type) {
      const typeTagMap = {
        1: 'primary',   // 交强险
        2: 'success',   // 商业险
        3: 'warning',   // 附加险
        4: 'info',      // 代收车船税
        5: ''           // 其他
      };
      return typeTagMap[type] || '';
    },
    
    // 搜索
    handleSearch() {
      this.pageNum = 1;
      this.getPackageList();
    },
    
    // 重置搜索
    handleReset() {
      this.searchData = {
        categoryName: "",
        type: null
      };
      this.pageNum = 1;
      this.getPackageList();
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pageSize = val;
      this.pageNum = 1;
      this.getPackageList();
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pageNum = val;
      this.getPackageList();
    },
    
    // 表格内操作
    handleEdit(row) {
      this.dialogType = 'edit';
      this.insuranceTypeVisible = true;
      this.$nextTick(() => {
        Object.keys(this.insuranceTypeForm).forEach(key => {
          this.insuranceTypeForm[key] = row[key];
        });
      });
    },
    
    handleDelete(row) {
      this.$confirm('确定要删除该险种吗？删除后不可恢复！', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$store.dispatch('insurance/postInsuranceTypesDelete',{
          categoryCode: row.categoryCode
        }).then(res => {
          if (res.code === '0') {
            this.$message.success('删除成功');
            this.getPackageList();
          } else {
            this.$message.error(res.msg || '删除失败');
          }
        }).catch(() => {
          this.$message.error('删除失败');
        });
      }).catch(() => {
        this.$message({
          type: 'info',
          message: '已取消删除'
        });
      });
    },
    
    // 新增险种
    handleAdd() {
      this.dialogType = 'add';
      this.insuranceTypeVisible = true;
      this.$nextTick(() => {
        this.$refs.insuranceTypeForm.resetFields();
        this.insuranceTypeForm = {
          categoryCode: '',
          categoryName: '',
          description: '',
          caseDesc: '',
          type: null
        };
      });
    },
    
    // 弹窗取消
    dialogCancel() {
      this.insuranceTypeVisible = false;
      this.$refs.insuranceTypeForm.resetFields();
    },
    
    // 弹窗确认
    dialogConfirm() {
      this.$refs.insuranceTypeForm.validate((valid) => {
        if (valid) {
          const action = this.dialogType === 'add' 
            ? 'insurance/postInsuranceTypesSave' 
            : 'insurance/postInsuranceTypesSave';
          
          this.$store.dispatch(action, {
            ...this.insuranceTypeForm
          }).then(res => {
            if (res.code === '0') {
              this.$message.success(this.dialogType === 'add' ? '新增成功' : '编辑成功');
              this.insuranceTypeVisible = false;
              this.getPackageList();
            } else {
              this.$message.error(res.msg || (this.dialogType === 'add' ? '新增失败' : '编辑失败'));
            }
          }).catch(() => {
            this.$message.error(this.dialogType === 'add' ? '新增失败' : '编辑失败');
          });
        } else {
          return false;
        }
      });
    },
    
    // 获取险种列表
    getPackageList() {
      this.listLoading = true;
      this.$store.dispatch('insurance/getInsuranceTypesList',{
        ...this.searchData,
        pageNum: this.pageNum,
        pageSize: this.pageSize,
      }).then(res => {
        if(res.code == '0'){
          this.listData = res.data.list || [];
          this.total = res.data.total || 0;
        } else {
          this.listData = [];
          this.total = 0;
          this.$message.error(res.msg || '获取数据失败');
        }
        this.listLoading = false;
      }).catch(() => {
        this.listData = [];
        this.total = 0;
        this.listLoading = false;
        this.$message.error('获取数据失败');
      });
    },
  },
  //生命周期 - 创建完成（可以访问当前this实例）
  created() {
    this.getPackageList();
  },
  //生命周期 - 挂载完成（可以访问DOM元素）
  mounted() {

  },
  //生命周期 - 创建之前
  beforeCreate() {},
  //生命周期 - 挂载之前
  beforeMount() {},
  //生命周期 - 更新之前
  beforeUpdate() {},
  //生命周期 - 更新之后
  updated() {},
  //生命周期 - 销毁之前
  beforeDestroy() {},
  //生命周期 - 销毁完成
  destroyed() {},
  //如果页面有keep-alive缓存功能，这个函数会触发
  activated() {},
}
</script>

<style lang="scss" scoped>
.insurance-types-page {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 84px);
  
  // 页面头部
  .page-header {
    margin-bottom: 24px;
    
    .header-content {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 12px;
      padding: 32px;
      color: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      
      .page-title {
        display: flex;
        align-items: center;
        margin: 0 0 12px 0;
        font-size: 28px;
        font-weight: 600;
        
        i {
          margin-right: 12px;
          font-size: 32px;
        }
      }
      
      .page-subtitle {
        margin: 0;
        font-size: 16px;
        opacity: 0.9;
      }
    }
  }
  
  // 搜索卡片
  .search-card {
    margin-bottom: 24px;
    border-radius: 12px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    
    .search-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      
      .search-title {
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        
        i {
          margin-right: 8px;
          color: #409EFF;
        }
      }
    }
    
    .search-form {
      ::v-deep .el-form-item {
        margin-bottom: 18px;
        
        .el-form-item__label {
          font-weight: 500;
          color: #606266;
        }
        
        .el-input__inner,
        .el-select {
          width: 100%;
        }
      }
    }
    
    .search-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 8px;
    }
  }
  
  // 数据卡片
  .data-card {
    border-radius: 12px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    
    .data-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 1px solid #ebeef5;
      
      .data-title {
        display: flex;
        align-items: center;
        gap: 16px;
        
        h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #303133;
        }
        
        .data-stats {
          font-size: 14px;
          color: #909399;
          
          .total-count {
            color: #409EFF;
            font-weight: 500;
          }
        }
      }
      
      .data-actions {
        .el-button {
          padding: 10px 20px;
          font-weight: 500;
        }
      }
    }
    
    // 表格容器
    .table-container {
      ::v-deep .modern-table {
        border-radius: 8px;
        overflow: hidden;
        
        .el-table__header {
          th {
            border-bottom: 2px solid #ebeef5;
            font-weight: 600;
          }
        }
        
        .el-table__body {
          tr {
            transition: background-color 0.2s;
            
            &:hover {
              background-color: #f5f7fa;
            }
          }
        }
        
        // 单元格样式
        .category-name-cell {
          .category-name {
            font-weight: 500;
            color: #303133;
          }
        }
        
        .description-cell,
        .case-desc-cell {
          color: #606266;
          line-height: 1.5;
        }
        
        .time-cell {
          color: #909399;
        }
        
        .action-cell {
          .danger-btn {
            color: #f56c6c;
          }
          
          ::v-deep .el-divider--vertical {
            margin: 0 8px;
          }
        }
      }
    }
    
    // 分页容器
    .pagination-container {
      margin-top: 24px;
      padding-top: 20px;
      border-top: 1px solid #ebeef5;
      display: flex;
      justify-content: center;
      
      ::v-deep .el-pagination {
        .el-pager li {
          border-radius: 6px;
          margin: 0 2px;
          
          &.active {
            background: #409EFF;
            border-color: #409EFF;
          }
        }
        
        .btn-prev, 
        .btn-next {
          border-radius: 6px;
        }
      }
    }
  }
  
  // 弹窗样式
  .insurance-dialog {
    ::v-deep .el-dialog__header {
      padding: 24px;
      border-bottom: 1px solid #ebeef5;
      margin: 0;
      
      .el-dialog__title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
      }
    }
    
    ::v-deep .el-dialog__body {
      padding: 32px;
    }
    
    ::v-deep .el-dialog__footer {
      padding: 24px;
      border-top: 1px solid #ebeef5;
    }
    
    .insurance-form {
      ::v-deep .el-form-item {
        margin-bottom: 24px;
        
        .el-form-item__label {
          font-weight: 500;
          color: #606266;
        }
        
        .el-radio-group {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          
          .el-radio {
            margin-right: 0;
          }
        }
        
        .el-textarea {
          .el-textarea__inner {
            border-radius: 6px;
            
            &:focus {
              border-color: #409EFF;
            }
          }
        }
      }
    }
  }
  
  // 响应式设计
  @media (max-width: 768px) {
    padding: 16px;
    
    .page-header {
      .header-content {
        padding: 24px;
        
        .page-title {
          font-size: 24px;
          
          i {
            font-size: 28px;
          }
        }
        
        .page-subtitle {
          font-size: 14px;
        }
      }
    }
    
    .search-card {
      .search-form {
        ::v-deep .el-row {
          .el-col {
            width: 100%;
            margin-bottom: 16px;
          }
        }
      }
      
      .search-actions {
        justify-content: center;
      }
    }
    
    .data-card {
      .data-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
      }
      
      .table-container {
        overflow-x: auto;
      }
    }
  }
}
</style>